import { COLLARS, unlockedCollar } from './care/Collars';
import { AWARDS } from './care/Awards';
import type { CareRecord } from './care/CareRecord';
import type { TrickLevels } from './world/Tricks';
import { TRICKS, TRICK_LABEL } from './commands';

/**
 * The inventory: one panel for everything that isn't the dog — items with
 * counts, the toy in hand, collars earned and locked, awards, tricks learned,
 * and doors to the album and the care panel.
 */
export interface InventorySource {
  bags: () => number;
  treats: () => number;
  toy: () => 'ball' | 'stick' | 'frisbee' | 'labubu';
  setToy: (k: 'ball' | 'stick' | 'frisbee' | 'labubu') => void;
  care: CareRecord;
  tricks: () => TrickLevels;
  dogName: string;
  openAlbum: () => void;
  openCare: () => void;
  openShop: () => void;
}

export class Inventory {
  private el: HTMLElement;
  constructor(private src: InventorySource) {
    this.el = document.querySelector<HTMLElement>('#inventory')!;
    this.el.querySelector<HTMLButtonElement>('.inv-close')!.addEventListener('click', () => this.close());
    this.el.querySelector<HTMLButtonElement>('#invAlbum')!.addEventListener('click', () => {
      this.close();
      src.openAlbum();
    });
    this.el.querySelector<HTMLButtonElement>('#invShop')!.addEventListener('click', () => {
      this.close();
      src.openShop();
    });
    this.el.querySelector<HTMLButtonElement>('#invCare')!.addEventListener('click', () => {
      this.close();
      src.openCare();
    });
  }

  get isOpen(): boolean {
    return !this.el.hidden;
  }

  open(): void {
    this.render();
    this.el.hidden = false;
  }

  close(): void {
    this.el.hidden = true;
  }

  private tile(icon: string, label: string, sub: string, opts: { locked?: boolean; active?: boolean; onClick?: () => void } = {}): HTMLElement {
    const t = document.createElement(opts.onClick ? 'button' : 'div');
    t.className = 'inv-tile' + (opts.locked ? ' locked' : '') + (opts.active ? ' active' : '');
    if (opts.onClick) {
      (t as HTMLButtonElement).type = 'button';
      t.addEventListener('click', opts.onClick);
    }
    t.innerHTML = `<span class="inv-icon"></span><span class="inv-label"></span><span class="inv-sub"></span>`;
    t.querySelector('.inv-icon')!.textContent = icon;
    t.querySelector('.inv-label')!.textContent = label;
    t.querySelector('.inv-sub')!.textContent = sub;
    return t;
  }

  render(): void {
    const s = this.src;
    const grid = (id: string): HTMLElement => {
      const g = this.el.querySelector<HTMLElement>(id)!;
      g.textContent = '';
      return g;
    };
    this.el.querySelector<HTMLElement>('.inv-title')!.textContent = `${s.dogName}'s things`;

    const items = grid('#invItems');
    items.append(this.tile('🥫', 'kibble', `${s.bags()} bag${s.bags() === 1 ? '' : 's'}`));
    items.append(this.tile('🦴', 'treats', `${s.treats()}`));
    items.append(this.tile('🎾', 'ball', s.toy() === 'ball' ? 'in hand' : 'tap to use', { active: s.toy() === 'ball', onClick: () => { s.setToy('ball'); this.render(); } }));
    items.append(this.tile('🪵', 'stick', s.toy() === 'stick' ? 'in hand' : 'tap to use', { active: s.toy() === 'stick', onClick: () => { s.setToy('stick'); this.render(); } }));
    items.append(this.tile('🥏', 'frisbee', s.toy() === 'frisbee' ? 'in hand' : 'tap to use', { active: s.toy() === 'frisbee', onClick: () => { s.setToy('frisbee'); this.render(); } }));
    items.append(this.tile('🧸', 'Labubu', s.toy() === 'labubu' ? 'in hand' : 'tap to use', { active: s.toy() === 'labubu', onClick: () => { s.setToy('labubu'); this.render(); } }));

    const best = s.care.state.bestStreak;
    const worn = unlockedCollar(best);
    const collars = grid('#invCollars');
    for (const c of COLLARS) {
      if (c.tier === 0) continue;
      const have = c.tier <= worn.tier;
      collars.append(this.tile(have ? '📿' : '🔒', c.name, have ? (c.tier === worn.tier ? 'wearing' : 'earned') : `${c.streak}-day streak`, { locked: !have }));
    }

    const tricks = grid('#invTricks');
    const lv = s.tricks();
    for (const t of TRICKS) {
      const n = Math.floor(lv[t]);
      tricks.append(this.tile(n >= 5 ? '⭐' : n > 0 ? '📖' : '·', TRICK_LABEL[t], n >= 5 ? 'mastered' : n > 0 ? `level ${n}` : 'say it to teach', { locked: n === 0 }));
    }

    const awards = grid('#invAwards');
    const streak = Math.max(s.care.currentStreak, best);
    for (const a of AWARDS) {
      const got = a.test(s.care.state, lv, streak);
      awards.append(this.tile(got ? a.icon : '🔒', a.name, got ? 'earned' : a.hint, { locked: !got }));
    }
  }
}
